import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { Utente } from '../../types';
import AccessoNegato from '../../components/permessi/AccessoNegato';
import GrigliaPermessi, {
  VoceGriglia, FlagsPermesso, CampoPermesso, OriginePermesso,
  TUTTO_ON, SOLA_LETT, TUTTO_OFF, normalizzaFlags, flagsUguali,
} from '../../components/permessi/GrigliaPermessi';
import { usePermesso } from '../../context/PermessiContext';
import '../../styles/impostazioni.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface VoceApi extends FlagsPermesso {
  modulo:  string;
  label:   string;
  origine: OriginePermesso;
  profilo: FlagsPermesso | null;
}

interface GruppoApi { gruppo: string; moduli: VoceApi[] }
interface MacroApi  { macro:  string; gruppi: GruppoApi[] }

interface ProfiloRef { id: number; nome: string }

interface ProfiloLista {
  id:     number;
  nome:   string;
  attivo: boolean;
}

interface RispostaPermessi {
  macroGruppi: MacroApi[];
  profilo:     ProfiloRef | null;
  eccezioni:   number;
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function PermessiPage() {
  const { isSuperadmin } = usePermesso();

  const [utenti,           setUtenti]           = useState<Utente[]>([]);
  const [profiliDisp,      setProfiliDisp]      = useState<ProfiloLista[]>([]);
  const [selectedUtenteId, setSelectedUtenteId] = useState<number | null>(null);
  const [selectedSocId,    setSelectedSocId]    = useState<number | null>(null);

  const [voci,        setVoci]        = useState<VoceGriglia[]>([]);
  const [origVoci,    setOrigVoci]    = useState<Record<string, FlagsPermesso>>({});
  const [profilo,     setProfilo]     = useState<ProfiloRef | null>(null);
  const [nEccezioni,  setNEccezioni]  = useState(0);

  const [loading,      setLoading]      = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [azione,       setAzione]       = useState('');
  const [msg,          setMsg]          = useState('');
  const [error,        setError]        = useState('');

  useEffect(() => {
    if (!isSuperadmin) return;
    // @api GET /api/utenti -> {"utenti":"Utente[]"}
    apiFetch<{ utenti: Utente[] }>('/api/utenti')
      .then(d => { setUtenti(d.utenti); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });

    // @api GET /api/profili -> {"success":"true","data":"ProfiloLista[]"}
    apiFetch<{ data: ProfiloLista[] }>('/api/profili')
      .then(d => setProfiliDisp(d.data.filter(p => p.attivo)))
      .catch(() => { /* la pagina resta usabile senza profili */ });
  }, [isSuperadmin]);

  const selectedUtente = utenti.find(u => u.id === selectedUtenteId);
  const filteredUtenti = utenti.filter(u => u.ruolo !== 'SUPERADMIN');
  const societa        = selectedUtente?.societa ?? [];
  const isDirty        = voci.some(v => v.dirty);

  // ── Caricamento ─────────────────────────────────────────────────────────
  const loadPermessi = useCallback(async (utenteId: number, societaId: number) => {
    setLoadingPerms(true);
    setMsg('');
    setError('');
    try {
      // @api GET /api/permessi?utenteId=N&societaId=N -> {"macroGruppi":"MacroApi[]","sezioni":"any[]","profilo":"ProfiloRef|null","eccezioni":"number"}
      const d = await apiFetch<RispostaPermessi>(
        `/api/permessi?utenteId=${utenteId}&societaId=${societaId}`
      );

      const flat: VoceGriglia[] = [];
      const orig: Record<string, FlagsPermesso> = {};
      for (const m of d.macroGruppi) {
        for (const g of m.gruppi) {
          for (const v of g.moduli) {
            flat.push({ ...v, macro: m.macro, gruppo: g.gruppo, dirty: false });
            orig[v.modulo] = {
              canView: v.canView, canEdit: v.canEdit,
              canDelete: v.canDelete, canExport: v.canExport,
            };
          }
        }
      }

      setVoci(flat);
      setOrigVoci(orig);
      setProfilo(d.profilo);
      setNEccezioni(d.eccezioni);
    } catch (e: any) { setError(e.message); }
    finally { setLoadingPerms(false); }
  }, []);

  const reset = () => {
    setVoci([]);
    setOrigVoci({});
    setProfilo(null);
    setNEccezioni(0);
    setMsg('');
    setError('');
  };

  const handleSelectUtente = (id: number | null) => {
    setSelectedUtenteId(id);
    setSelectedSocId(null);
    reset();
  };

  const handleSelectSocieta = (id: number | null) => {
    setSelectedSocId(id);
    reset();
    if (id && selectedUtenteId) loadPermessi(selectedUtenteId, id);
  };

  const ricarica = () => {
    if (selectedUtenteId && selectedSocId) loadPermessi(selectedUtenteId, selectedSocId);
  };

  // ── Modifica griglia ────────────────────────────────────────────────────
  const marcaDirty = (v: VoceGriglia): VoceGriglia => ({
    ...v,
    dirty: !flagsUguali(v, origVoci[v.modulo] ?? TUTTO_OFF),
  });

  const toggle = (modulo: string, campo: CampoPermesso) => {
    setVoci(prev => prev.map(v => {
      if (v.modulo !== modulo) return v;
      return marcaDirty({ ...v, ...normalizzaFlags({ ...v, [campo]: !v[campo] }) });
    }));
  };

  const applicaGruppo = (filtro: (v: VoceGriglia) => boolean, flags: FlagsPermesso) => {
    setVoci(prev => prev.map(v => (filtro(v) ? marcaDirty({ ...v, ...normalizzaFlags(flags) }) : v)));
  };

  // Riporta una singola voce ai valori del profilo. Al salvataggio il backend
  // riconosce che coincide col profilo e cancella la deroga.
  const ripristinaDaProfilo = (modulo: string) => {
    setVoci(prev => prev.map(v => {
      if (v.modulo !== modulo || !v.profilo) return v;
      return marcaDirty({ ...v, ...normalizzaFlags(v.profilo) });
    }));
  };

  // ── Azioni ──────────────────────────────────────────────────────────────
  const eseguiAzione = async (
    etichetta: string,
    chiamata: () => Promise<string>,
  ) => {
    setAzione(etichetta);
    setMsg('');
    setError('');
    try {
      const esito = await chiamata();
      setMsg(esito);
      ricarica();
    } catch (e: any) { setError(e.message); }
    finally { setAzione(''); }
  };

  const assegnaProfilo = (profiloId: number, tutteLeSocieta: boolean) => {
    if (!selectedUtenteId || !selectedSocId) return;
    if (isDirty && !window.confirm('Ci sono modifiche non salvate che andranno perse. Continuare?')) return;

    const nome = profiliDisp.find(p => p.id === profiloId)?.nome ?? '';
    eseguiAzione('assegna', async () => {
      // @api POST /api/profili/:id/assegna {"utenteId":"number","societaIds":"number[]","tutteLeSocieta":"boolean","azzeraEccezioni":"boolean"} -> {"success":"true","data":"{societaAggiornate:number}"}
      const r = await apiFetch<{ data: { societaAggiornate: number } }>(
        `/api/profili/${profiloId}/assegna`,
        {
          method: 'POST',
          body: JSON.stringify({
            utenteId:       selectedUtenteId,
            societaIds:     tutteLeSocieta ? undefined : [selectedSocId],
            tutteLeSocieta: tutteLeSocieta || undefined,
          }),
        },
      );
      return tutteLeSocieta
        ? `Profilo "${nome}" assegnato su ${r.data.societaAggiornate} società.`
        : `Profilo "${nome}" assegnato.`;
    });
  };

  const sganciaProfilo = () => {
    if (!selectedUtenteId || !selectedSocId) return;
    if (!window.confirm('Sganciare l\'utente dal profilo? I permessi attuali restano come deroghe individuali.')) return;

    eseguiAzione('sgancia', async () => {
      // @api DELETE /api/profili/assegnazione/:utenteId/:societaId -> {"success":"true"}
      await apiFetch(`/api/profili/assegnazione/${selectedUtenteId}/${selectedSocId}`, { method: 'DELETE' });
      return 'Profilo rimosso. I permessi attuali sono stati mantenuti come deroghe.';
    });
  };

  const allineaAlProfilo = () => {
    if (!selectedUtenteId || !selectedSocId) return;
    if (!window.confirm(`Rimuovere tutte le ${nEccezioni} deroghe? L'utente tornerà a essere esattamente il profilo "${profilo?.nome}".`)) return;

    eseguiAzione('allinea', async () => {
      // @api POST /api/permessi/allinea-profilo {"utenteId":"number","societaId":"number"} -> {"success":"true","data":"{eccezioniRimosse:number}"}
      const r = await apiFetch<{ data: { eccezioniRimosse: number } }>('/api/permessi/allinea-profilo', {
        method: 'POST',
        body: JSON.stringify({ utenteId: selectedUtenteId, societaId: selectedSocId }),
      });
      return `${r.data.eccezioniRimosse} deroghe rimosse: l'utente ora segue interamente il profilo.`;
    });
  };

  const copiaSuTutteLeSocieta = () => {
    if (!selectedUtenteId || !selectedSocId) return;
    const nomeSoc = societa.find(s => s.id === selectedSocId)?.nome ?? '';
    if (!window.confirm(`Copiare profilo e deroghe di "${nomeSoc}" su tutte le altre società dell'utente? La configurazione esistente su quelle società verrà sostituita.`)) return;

    eseguiAzione('copia', async () => {
      // @api POST /api/permessi/copia-societa {"utenteId":"number","daSocietaId":"number","tutteLeSocieta":"boolean"} -> {"success":"true","data":"{societaAggiornate:number}"}
      const r = await apiFetch<{ data: { societaAggiornate: number } }>('/api/permessi/copia-societa', {
        method: 'POST',
        body: JSON.stringify({
          utenteId:       selectedUtenteId,
          daSocietaId:    selectedSocId,
          tutteLeSocieta: true,
        }),
      });
      return `Configurazione copiata su ${r.data.societaAggiornate} altre società.`;
    });
  };

  // ── Salvataggio ─────────────────────────────────────────────────────────
  const save = async () => {
    if (!selectedUtenteId || !selectedSocId) return;
    setSaving(true);
    setMsg('');
    setError('');
    try {
      // @api PUT /api/permessi {"utenteId":"number","societaId":"number","permessi":"VoceApi[]"} -> {"ok":"true","eccezioniScritte":"number","eccezioniRimosse":"number"}
      await apiFetch('/api/permessi', {
        method: 'PUT',
        body: JSON.stringify({
          utenteId:  selectedUtenteId,
          societaId: selectedSocId,
          permessi:  voci.map(({ modulo, canView, canEdit, canDelete, canExport }) =>
            ({ modulo, canView, canEdit, canDelete, canExport })),
        }),
      });
      // Ricarico dal server: l'origine di ogni riga (eredita / deroga) la decide
      // il backend confrontando col profilo, non è ricostruibile lato client.
      await loadPermessi(selectedUtenteId, selectedSocId);
      setMsg('Permessi salvati con successo.');
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (!isSuperadmin) {
    return (
      <Layout>
        <AccessoNegato messaggio="Solo un Superadmin può gestire i permessi degli utenti." />
      </Layout>
    );
  }

  const inCorso = azione !== '';

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Permessi</h2>
          <p>
            Assegna un profilo e correggi solo le eccezioni. Le voci marcate
            <span className="impostazioni-permessi-origine impostazioni-permessi-origine--profilo"> da profilo </span>
            seguono automaticamente ogni modifica al profilo; quelle marcate
            <span className="impostazioni-permessi-origine impostazioni-permessi-origine--eccezione"> eccezione </span>
            no.
          </p>
        </div>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      {/* ── Selettori ─────────────────────────────────────────────────── */}
      <div className="bop-card impostazioni-permessi-selettori">
        <div className="impostazioni-permessi-selettori-row">
          <div className="impostazioni-permessi-selettori-group">
            <label className="impostazioni-permessi-label">Utente:</label>
            <select
              className="bop-select impostazioni-permessi-select-utente"
              value={selectedUtenteId ?? ''}
              onChange={e => handleSelectUtente(e.target.value ? Number(e.target.value) : null)}
              disabled={loading}
            >
              <option value="">— Seleziona un utente —</option>
              {filteredUtenti.map(u => (
                <option key={u.id} value={u.id}>{u.nome} ({u.email})</option>
              ))}
            </select>
          </div>

          {selectedUtenteId && (
            <div className="impostazioni-permessi-selettori-group">
              <label className="impostazioni-permessi-label">Società:</label>
              <select
                className="bop-select impostazioni-permessi-select-societa"
                value={selectedSocId ?? ''}
                onChange={e => handleSelectSocieta(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— Seleziona una società —</option>
                {societa.map(s => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── Contenuto ─────────────────────────────────────────────────── */}
      {selectedUtenteId && selectedSocId && (
        <>
          {loadingPerms ? (
            <div className="bop-card"><div className="bop-empty-state">Caricamento permessi...</div></div>
          ) : voci.length === 0 ? (
            <div className="bop-card"><div className="bop-empty-state">Nessun modulo disponibile.</div></div>
          ) : (
            <>
              {/* ── Barra profilo ──────────────────────────────────────── */}
              <div className="bop-card impostazioni-profilo-barra">
                <div className="impostazioni-profilo-barra-campo">
                  <label className="impostazioni-permessi-label">Profilo:</label>
                  <select
                    className="bop-select impostazioni-profilo-barra-select"
                    value={profilo?.id ?? ''}
                    disabled={inCorso || profiliDisp.length === 0}
                    onChange={e => {
                      if (!e.target.value) sganciaProfilo();
                      else assegnaProfilo(Number(e.target.value), false);
                    }}
                  >
                    <option value="">
                      {profiliDisp.length === 0 ? '— Nessun profilo definito —' : '— Nessun profilo —'}
                    </option>
                    {profiliDisp.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="impostazioni-profilo-barra-info">
                  {profilo
                    ? (nEccezioni === 0
                        ? 'Segue il profilo su tutte le voci.'
                        : `${nEccezioni} ${nEccezioni === 1 ? 'deroga' : 'deroghe'} rispetto al profilo.`)
                    : `Permessi individuali${nEccezioni ? ` su ${nEccezioni} voci` : ''}: nessun profilo assegnato.`}
                </div>

                <div className="impostazioni-profilo-barra-spacer" />

                <div className="impostazioni-profilo-barra-azioni">
                  {profilo && (
                    <button
                      className="bop-btn bop-btn-ghost bop-btn-sm"
                      disabled={inCorso || nEccezioni === 0}
                      onClick={allineaAlProfilo}
                      title="Rimuove tutte le deroghe: l'utente torna a essere esattamente il profilo"
                    >
                      {azione === 'allinea' ? 'Allineo…' : 'Allinea al profilo'}
                    </button>
                  )}
                  {profilo && societa.length > 1 && (
                    <button
                      className="bop-btn bop-btn-ghost bop-btn-sm"
                      disabled={inCorso}
                      onClick={() => assegnaProfilo(profilo.id, true)}
                      title="Assegna questo profilo su tutte le società dell'utente"
                    >
                      {azione === 'assegna' ? 'Assegno…' : `Applica a tutte le ${societa.length} società`}
                    </button>
                  )}
                  {societa.length > 1 && (
                    <button
                      className="bop-btn bop-btn-ghost bop-btn-sm"
                      disabled={inCorso}
                      onClick={copiaSuTutteLeSocieta}
                      title="Copia profilo e deroghe di questa società su tutte le altre"
                    >
                      {azione === 'copia' ? 'Copio…' : 'Copia su altre società'}
                    </button>
                  )}
                </div>
              </div>

              {/* ── Azioni globali griglia ─────────────────────────────── */}
              <div className="impostazioni-permessi-actions-global">
                <button className="bop-btn bop-btn-ghost bop-btn-sm"
                  onClick={() => applicaGruppo(() => true, TUTTO_ON)}>
                  Tutto pieno
                </button>
                <button className="bop-btn bop-btn-ghost bop-btn-sm"
                  onClick={() => applicaGruppo(() => true, SOLA_LETT)}>
                  Solo lettura
                </button>
                <button className="bop-btn bop-btn-ghost bop-btn-sm impostazioni-permessi-btn-danger"
                  onClick={() => applicaGruppo(() => true, TUTTO_OFF)}>
                  Azzera tutto
                </button>
              </div>

              <GrigliaPermessi
                voci={voci}
                onToggle={toggle}
                onApplicaGruppo={applicaGruppo}
                mostraEredita
                onRipristina={profilo ? ripristinaDaProfilo : undefined}
                disabilitata={inCorso}
              />

              {/* ── Salva ──────────────────────────────────────────────── */}
              <div className="impostazioni-permessi-footer">
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={save}
                  disabled={saving || inCorso || !isDirty}
                >
                  {saving ? 'Salvo…' : 'Salva permessi'}
                </button>
                {msg   && <span className="impostazioni-permessi-msg-success">{msg}</span>}
                {error && <span className="impostazioni-permessi-msg-error">{error}</span>}
                {isDirty && !saving && (
                  <span className="impostazioni-permessi-msg-warning">Modifiche non salvate</span>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Layout>
  );
}
